import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, FileText, UploadCloud, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPost } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';

type Row = Record<string, string | number>;
interface ImportResult {
  created: number;
  skipped: number;
  rows: { row: number; status: 'created' | 'failed'; name?: string; reason?: string }[];
}

const EXPECTED = [
  'name',
  'selling_price',
  'purchase_price',
  'mrp',
  'unit',
  'hsn_code',
  'gst_rate',
  'sku',
  'barcode',
  'brand',
  'category',
];

/**
 * Bulk product import. Accepts a CSV (header-driven) — same parser
 * shape as the Flutter side so the same template works on both.
 *
 * Flow: pick file → preview first 8 rows → confirm → POST to
 * `/products/bulk-import`. The backend creates rows + auto-creates
 * brands/categories by name. We surface the per-row breakdown.
 */
export function BulkImportPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const importMut = useMutation({
    mutationFn: (rowsToSend: Row[]) =>
      apiPost<ImportResult>(endpoints.productBulkImport, { rows: rowsToSend }),
    onSuccess: (data) => {
      toast.success(`${data.created} products imported`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onPick = async (file: File) => {
    setFileName(file.name);
    setParseError(null);
    setRows([]);
    importMut.reset();

    if (!file.name.toLowerCase().match(/\.(csv|txt)$/)) {
      setParseError('Please pick a .csv file');
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      setRows(parsed);
    } catch (err) {
      setParseError((err as Error).message);
    }
  };

  return (
    <>
      <PageHeader
        back
        title="Bulk product import"
        description="Upload a CSV to add or update many products at once."
      />

      <Card className="mb-4">
        <SectionHeader title="CSV format" />
        <p className="text-sm text-ink-soft">
          The first row is the header. Required columns: <Code>name</Code>,{' '}
          <Code>selling_price</Code>. Optional: <Code>purchase_price</Code>,{' '}
          <Code>mrp</Code>, <Code>unit</Code>, <Code>hsn_code</Code>,{' '}
          <Code>gst_rate</Code> (0/5/12/18/28), <Code>sku</Code>,{' '}
          <Code>barcode</Code>, <Code>brand</Code>, <Code>category</Code>. New
          brands and categories are created automatically.
        </p>
        <button
          type="button"
          onClick={() => downloadTemplate()}
          className="mt-3 text-sm font-medium text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
        >
          <FileText className="h-4 w-4" /> Download template
        </button>
      </Card>

      {/* Picker */}
      <Card className="mb-4">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
          }}
        />
        <div className="flex flex-col items-center justify-center text-center py-6">
          <div className="h-14 w-14 rounded-2xl bg-brand-500/10 text-brand-700 grid place-items-center mb-3">
            <UploadCloud className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold">
            {fileName ?? 'Drop or pick a CSV'}
          </h3>
          <p className="text-sm text-ink-soft mt-1 max-w-md">
            We'll preview the first 8 rows so you can confirm before import.
          </p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => inputRef.current?.click()}>
              Pick file
            </Button>
            {fileName && (
              <Button
                variant="outline"
                onClick={() => {
                  setFileName(null);
                  setRows([]);
                  setParseError(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                Reset
              </Button>
            )}
          </div>
          {parseError && (
            <p className="text-sm text-danger mt-3">{parseError}</p>
          )}
        </div>
      </Card>

      {/* Preview */}
      {rows.length > 0 && !importMut.data && (
        <Card padded={false} className="mb-4">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold">{rows.length} valid rows</h2>
              <p className="text-xs text-ink-soft">Showing first 8</p>
            </div>
            <Button
              loading={importMut.isPending}
              onClick={() => importMut.mutate(rows)}
            >
              Import {rows.length} products
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                  <th className="px-5 py-2">Name</th>
                  <th className="px-5 py-2 text-right">Selling</th>
                  <th className="px-5 py-2">Brand</th>
                  <th className="px-5 py-2">Category</th>
                  <th className="px-5 py-2">SKU</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="px-5 py-2 font-medium">{r.name as string}</td>
                    <td className="px-5 py-2 text-right tabular-nums">
                      <Money value={Number(r.selling_price)} />
                    </td>
                    <td className="px-5 py-2 text-ink-soft">
                      {(r.brand as string) || '—'}
                    </td>
                    <td className="px-5 py-2 text-ink-soft">
                      {(r.category as string) || '—'}
                    </td>
                    <td className="px-5 py-2 font-mono text-xs">
                      {(r.sku as string) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Result */}
      {importMut.data && (
        <Card>
          <SectionHeader
            title="Import complete"
            description={`Created ${importMut.data.created}, skipped ${importMut.data.skipped}.`}
            actions={
              <Button onClick={() => navigate('/products')}>Back to products</Button>
            }
          />
          <ul className="space-y-1.5 max-h-80 overflow-auto">
            {importMut.data.rows.map((r) => (
              <li
                key={r.row}
                className="flex items-start gap-2 text-sm border-b border-line py-1.5"
              >
                {r.status === 'created' ? (
                  <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-4 w-4 text-danger flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <span className="text-xs text-ink-soft">Row {r.row}</span>{' '}
                  <span className="font-medium">{r.name ?? '—'}</span>
                  {r.reason && (
                    <div className="text-xs text-danger mt-0.5">{r.reason}</div>
                  )}
                </div>
                <Pill tone={r.status === 'created' ? 'success' : 'danger'}>
                  {r.status}
                </Pill>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-xs bg-surface-2 px-1.5 py-0.5 rounded">
      {children}
    </code>
  );
}

function downloadTemplate() {
  const csv =
    EXPECTED.join(',') +
    '\n' +
    [
      'Bisleri 1L,20,12,25,piece,22011010,12,BIS-1L,8901234567890,Bisleri,Beverages',
      'Lays 50g,20,14,25,piece,21052010,12,LAY-50,8901234567891,Lays,Snacks',
    ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'new-vishwas-products-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ───────────────────── CSV parsing ───────────────────── */

function parseCsv(text: string): Row[] {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) throw new Error('File is empty');
  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  if (!header.includes('name') || !header.includes('selling_price')) {
    throw new Error('CSV must include `name` and `selling_price` columns');
  }
  const rows: Row[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const row: Row = {};
    for (let j = 0; j < header.length && j < cells.length; j++) {
      const col = header[j];
      if (!EXPECTED.includes(col)) continue;
      const v = cells[j].trim();
      if (!v) continue;
      if (
        col === 'selling_price' ||
        col === 'purchase_price' ||
        col === 'mrp' ||
        col === 'gst_rate'
      ) {
        const n = Number(v);
        if (!Number.isNaN(n)) row[col] = n;
      } else {
        row[col] = v;
      }
    }
    if (typeof row.name === 'string' && row.selling_price !== undefined) rows.push(row);
  }
  if (rows.length === 0) throw new Error('No valid rows found');
  return rows;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        buf += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === ',' && !inQuotes) {
      out.push(buf);
      buf = '';
    } else {
      buf += c;
    }
  }
  out.push(buf);
  return out;
}
